import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Select, Table, Typography, Alert, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchCronJobs } from '../../services/cronLogService';
import HistoryModal from './HistoryModal';

const STATUS_OPTIONS = [
  { label: 'Tất cả', value: 'all' },
  { label: 'Chưa triển khai', value: 'not_implemented' },
  { label: 'Hôm nay chưa chạy', value: 'not_start_today' },
  { label: 'Thành công hôm nay', value: 'success' },
  { label: 'Thất bại hôm nay', value: 'fail' },
  { label: 'Timeout', value: 'time_out' },
  { label: 'Đang chạy', value: 'running' },
];

const getStatusConfig = (status) => {
  switch (status) {
    case 'not_implemented':
      return {
        badgeStatus: 'default',
        text: 'Chưa triển khai',
        tooltip: 'Job chưa được triển khai/giám sát chính thức.',
      };
    case 'not_start_today':
      return {
        badgeStatus: 'warning',
        text: 'Hôm nay chưa chạy',
        tooltip: 'Hôm nay chưa ghi nhận lần chạy nào.',
      };
    case 'success':
      return {
        badgeStatus: 'success',
        text: 'Thành công',
        tooltip: 'Lần chạy gần nhất hôm nay thành công.',
      };
    case 'fail':
      return {
        badgeStatus: 'error',
        text: 'Thất bại',
        tooltip: 'Lần chạy gần nhất hôm nay thất bại.',
      };
    case 'time_out':
      return {
        badgeStatus: 'error',
        text: 'Timeout',
        tooltip: 'Lần chạy gần nhất hôm nay bị timeout.',
      };
    case 'running':
      return {
        badgeStatus: 'processing',
        text: 'Đang chạy',
        tooltip: 'Đang có một lần chạy đang diễn ra (chưa kết thúc).',
      };
    default:
      return {
        badgeStatus: 'default',
        text: status || 'Không xác định',
        tooltip: '',
      };
  }
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    return date.toLocaleString('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour12: false,
    });
  } catch (e) {
    return value;
  }
};

const formatDuration = (durationMs) => {
  if (durationMs == null) return '';
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds} giây`;
  return `${minutes} phút ${seconds.toString().padStart(2, '0')} giây`;
};

const CronJobPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const loadJobs = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchCronJobs();
      if (!data?.status) {
        setError('Không thể tải danh sách cron job.');
        setJobs([]);
      } else {
        setJobs(data?.data?.data || []);
      }
    } catch (e) {
      setError('Có lỗi xảy ra khi tải danh sách cron job.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const name = job.name || '';
      const status = job.status || '';
      const matchSearch = name.toLowerCase().includes(searchText.trim().toLowerCase());
      const matchStatus = statusFilter === 'all' ? true : status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [jobs, searchText, statusFilter]);

  const handleOpenHistory = (job) => {
    setSelectedJob(job);
    setHistoryModalOpen(true);
  };

  const columns = [
    {
      title: 'Tên job',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Lịch chạy',
      dataIndex: 'schedule',
      key: 'schedule',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const config = getStatusConfig(value);
        return (
          <Tooltip title={config.tooltip || undefined}>
            <Badge status={config.badgeStatus} text={config.text} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Thời điểm chạy gần nhất',
      dataIndex: 'lastStartedAt',
      key: 'lastStartedAt',
      render: (value) => (value ? formatDateTime(value) : '—'),
    },
    {
      title: 'Thời gian',
      dataIndex: 'lastDurationMs',
      key: 'lastDurationMs',
      render: (value, record) => {
        if (!value && record.status === 'running') return 'Đang chạy';
        if (value == null) return '';
        return formatDuration(value);
      },
    },
    {
      title: 'Tổng số lần chạy',
      dataIndex: 'totalRuns',
      key: 'totalRuns',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleOpenHistory(record)}>
          Xem lịch sử
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <Typography.Title level={3} className="!mb-0">
          Quản lý Cron Job
        </Typography.Title>
        <Button type="primary" onClick={loadJobs} loading={loading}>
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Tìm kiếm theo tên job"
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 320 }}
          />
          <Button
            size="large"
            icon={<SearchOutlined />}
            onClick={() => setSearchText(searchText)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span>Trạng thái:</span>
          <Select
            size="large"
            style={{ width: 200 }}
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
          />
        </div>
      </div>

      {error && (
        <Alert type="error" message={error} showIcon className="mb-2" />
      )}

      <Table
        rowKey={(record) => record.logName}
        loading={loading}
        dataSource={filteredJobs}
        columns={columns}
        pagination={{ pageSize: 20 }}
        size="middle"
      />

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        jobLogName={selectedJob?.logName}
        jobDisplayName={selectedJob?.name || selectedJob?.logName}
      />
    </div>
  );
};

export default CronJobPage;

